import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import tradingService from '../services/tradingService';

const useTradingStore = create(persist((set, get) => ({
  // State
  selectedPair: 'BTC-USDT',
  pairs: [],
  marketSummary: {},
  orderBook: { bids: [], asks: [] },
  tradeHistory: [],
  userOrders: [],
  balances: {},
  ticker: {},
  isLoading: false,
  error: null,

  // Actions
  setSelectedPair: (pair) => {
    set({ selectedPair: pair });
    // Fetch data for the new pair
    get().fetchOrderBook(pair);
    // get().fetchTradeHistory(pair);
  },

  fetchTradingPairs: async () => {
    set({ isLoading: true, error: null });
    try {
      const pairs = await tradingService.getTradingPairs();
      set({ pairs: pairs || [], isLoading: false });
      return pairs || [];
    } catch (error) {
      console.error('Error fetching trading pairs:', error);
      set({ error: error.message, pairs: [], isLoading: false });
      // Don't throw error to prevent app crash
      return [];
    }
  },


  fetchTicker: async (pair = null) => {
    try {
      const ticker = await tradingService.getTicker(pair);
      set({ ticker });
      return ticker;
    } catch (error) {
      set({ error: error.message });
      throw error;
    }
  },

  fetchOrderBook: async (pair) => {
    try {
      const orderBook = await tradingService.getOrderBook(pair);
      set({ orderBook: orderBook || { bids: [], asks: [] } });
      return orderBook || { bids: [], asks: [] };
    } catch (error) {
      console.error('Error fetching order book:', error);
      set({ error: error.message, orderBook: { bids: [], asks: [] } });
      return { bids: [], asks: [] };
    }
  },

  fetchTradeHistory: async (pair, limit = 50) => {
    try {
      const tradeHistory = await tradingService.getTradeHistory(pair, limit);
      set({ tradeHistory: tradeHistory || [] });
      return tradeHistory || [];
    } catch (error) {
      console.error('Error fetching trade history:', error);
      set({ error: error.message, tradeHistory: [] });
      return [];
    }
  },

  fetchRecentTrades: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const response = await tradingService.getRecentTrades(params);
      // Handle paginated response
      const recentTrades = response?.results || response || [];
      set({ tradeHistory: recentTrades, isLoading: false });
      return response; // Return full response with pagination info
    } catch (error) {
      console.error('Error fetching recent trades:', error);
      set({ error: error.message, tradeHistory: [], isLoading: false });
      return { results: [], count: 0, next: null, previous: null };
    }
  },

  fetchUserOrders: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const response = await tradingService.getUserOrdersJWT(params);
      // Handle paginated response
      const userOrders = response?.results || response || [];
      set({ userOrders, isLoading: false });
      return response; // Return full response with pagination info
    } catch (error) {
      console.error('Error fetching user orders:', error);
      set({ error: error.message, userOrders: [], isLoading: false });
      return { results: [], count: 0 };
    }
  },

  fetchBalances: async () => {
    set({ isLoading: true, error: null });
    try {
      const balances = await tradingService.getBalancesJWT();
      set({ balances: balances || {}, isLoading: false });
      return balances || {};
    } catch (error) {
      console.error('Error fetching balances:', error);
      set({ error: error.message, balances: {}, isLoading: false });
      return {};
    }
  },

  placeOrder: async (orderData) => {
    set({ isLoading: true, error: null });
    try {
      const result = await tradingService.placeOrderJWT(orderData);
      // Refresh user orders and balances after placing order
      get().fetchUserOrders();
      get().fetchBalances();
      set({ isLoading: false });
      return result;
    } catch (error) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  cancelOrder: async (orderId) => {
    set({ isLoading: true, error: null });
    try {
      const result = await tradingService.cancelOrderJWT(orderId);
      // Refresh user orders and balances after canceling order
      get().fetchUserOrders();
      get().fetchBalances();
      set({ isLoading: false });
      return result;
    } catch (error) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  // WebSocket related actions
  updateOrderBook: (orderBook) => {
    set({ orderBook });
  },

  updateTicker: (ticker) => {
    set({ ticker });
  },

  addTradeToHistory: (trade) => {
    const { tradeHistory } = get();
    set({ 
      tradeHistory: [trade, ...tradeHistory.slice(0, 49)] // Keep last 50 trades
    });
  },

  updateUserOrder: (order) => {
    const { userOrders } = get();
    const updatedOrders = userOrders.map(o => 
      o.id === order.id ? { ...o, ...order } : o
    );
    set({ userOrders: updatedOrders });
  },

  removeUserOrder: (orderId) => {
    const { userOrders } = get();
    const updatedOrders = userOrders.filter(o => o.id !== orderId);
    set({ userOrders: updatedOrders });
  },

  clearError: () => {
    set({ error: null });
  },

  setLoading: (loading) => {
    set({ isLoading: loading });
  },

  // Utility functions
  getPairPrice: (pair) => {
    const { ticker, marketSummary } = get();
    return ticker[pair]?.last_price || marketSummary.data?.[pair]?.last_price || 0;
  },

  getPairChange: (pair) => {
    const { ticker, marketSummary } = get();
    return ticker[pair]?.percent_change || marketSummary.data?.[pair]?.percent_change || 0;
  },

  getBalance: (currency) => {
    const { balances } = get();
    return balances[currency] || { actual: 0, orders: 0 };
  },
}), {
  name: 'trading-preferences',
  partialize: (state) => ({ selectedPair: state.selectedPair }),
}));

export default useTradingStore; 
